import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { ArrowRight, Target, Zap, TrendingUp, Users, MessageSquare, BarChart3 } from "lucide-react";

const AISocialCampaign = () => {
  const features = [
    {
      icon: Target,
      title: "AI Brand Scan",
      description: "Our AI scans your brand, competitors, and target audience to build a data-driven strategy."
    },
    {
      icon: Zap,
      title: "Smart Content Engine",
      description: "AI generates optimized posts, captions, and visuals that engage and convert."
    },
    {
      icon: TrendingUp,
      title: "Campaign Deployment",
      description: "We launch AI-optimized campaigns across your chosen platforms."
    },
    {
      icon: MessageSquare,
      title: "Autopilot Nurturing",
      description: "Intelligent chatbots and SMS/email automations follow up with every lead automatically."
    },
    {
      icon: BarChart3,
      title: "Real-Time Optimization",
      description: "AI monitors every ad and post, reallocating budget and focus where results are strongest."
    }
  ];

  const benefits = [
    "Customized AI Social Playbook for your business",
    "Daily/weekly automated post scheduling",
    "Integrated ad + organic funnel strategy",
    "Full analytics dashboard with live performance metrics",
    "Lead nurturing system connected to your CRM or GoHighLevel"
  ];

  const idealFor = [
    {
      title: "Local Businesses",
      description: "Wanting more inbound leads"
    },
    {
      title: "Marketing Agencies",
      description: "Looking to scale client campaigns"
    },
    {
      title: "eCommerce Brands",
      description: "Seeking consistent traffic"
    },
    {
      title: "Service-Based Businesses",
      description: "That want appointments booked automatically"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="AI Social Campaign Accelerator - Neil AI Global Solutions"
        description="Turn your social media into a 24/7 lead generation engine with AI-powered automation for Facebook, Instagram, and LinkedIn."
        keywords="AI social media, campaign automation, lead generation, Facebook automation, Instagram marketing, LinkedIn campaigns"
      />
      <Navigation />
      
      <main className="pt-40 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 text-lg px-6 py-2">AI Social Campaign Accelerator</Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
            Turn Your Social Media Into a 24/7 Lead Generation Engine
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            The AI Social Campaign Accelerator by Neil AI Global Solutions is an advanced, done-for-you system that transforms your Facebook, Instagram, and LinkedIn pages into powerful marketing machines.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" asChild className="text-lg">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                Schedule Free Demo <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button size="lg" variant="secondary" asChild className="text-lg">
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">
                Start Your Campaign
              </a>
            </Button>
          </div>
        </section>

        {/* What It Does */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">💡 What It Does</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              The AI Social Campaign Accelerator uses smart automation to supercharge your social media presence
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-lg">✅ Create and post engaging content that matches your brand voice and audience interests.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-lg">✅ Identify and target your ideal customers using predictive analytics and AI behavior modeling.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-lg">✅ Automate follow-ups and nurturing, turning cold leads into ready-to-buy prospects.</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-lg">✅ Track and optimize ad performance in real time to lower costs and boost ROI.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How It Works */}
        <section className="container mx-auto px-4 py-16 bg-muted/30 rounded-2xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">⚙️ How It Works</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* What You Get */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">📈 What You Get</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-8">
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <span className="text-primary text-2xl">✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Why It Works */}
        <section className="container mx-auto px-4 py-16 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5 rounded-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">🧠 Why It Works</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our system blends AI intelligence with human marketing expertise—so you get data-driven campaigns that still feel personal, authentic, and emotionally engaging.
            </p>
            <p className="text-2xl font-bold text-primary mt-6">
              The result? More visibility, more engagement, and more conversions.
            </p>
          </div>
        </section>

        {/* Perfect For */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">💬 Perfect For</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {idealFor.map((item, index) => (
              <Card key={index} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Pricing CTA */}
        <section className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center border-4 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                🔥 Ready to Accelerate?
              </CardTitle>
              <p className="text-xl text-muted-foreground">
                Let Neil AI Global Solutions set up your AI Social Campaign Accelerator today and watch your brand go from invisible to unstoppable.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-5xl font-bold text-primary">
                $59<span className="text-2xl">/mo</span>
              </div>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button size="lg" asChild className="text-lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">
                    Schedule Free Demo <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="lg" variant="secondary" asChild className="text-lg">
                  <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">
                    Start Campaign Now
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AISocialCampaign;